import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./storeform.scss";
import BreadCrumsHeader from "../../common/BreadCrums";
import FormLayout from "../../common/FormLayout";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  franchiseSchema,
  uploadOfferSchema,
} from "../../../utils/validationSchemas";
import {
  addFranchise,
  editFranchise,
  getFranchises,
} from "../../../services/franchiseService";
import Loader from "../../common/Loader";
import dayjs from "dayjs";
import AddStore from "./AddStore";
// import { ReactComponent as UploadIcon } from "../../../Assets/Icons/uploadExcelIcon.svg";
// import { ReactComponent as AddIcon } from "../../../Assets/Icons/addNewIcon.svg";
import CSVUpload from "../../common/OfferForm/CSVUpload";
import toast from "../../../utils/toast";
import {
  inferCountryFromPhoneNumber,
  normalizePhoneNumberForInput,
} from "../../../utils/phoneValidation";

const StoreFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.franchiseReducer?.loading);
  const [uploadClicked] = useState(false);
  const allFranchises = useSelector(
    (state) => state.franchiseReducer.allFranchises
  );
  const { id = null } = location?.state || "";
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const format = "hh:mm A";

  const [initialValues, setInitialValues] = useState({
    document: "",
    franchiseName: "",
    email: "",
    contact: "",
    contactCountry: "AU",
    merchantName: "",
    address: "",
    timeSet: false,
    timeData: days?.map((d) => ({
      day: d?.toLowerCase(),
      openTime: undefined,
      closeTime: undefined,
      isSelected: false,
    })),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema:
      uploadClicked === true ? uploadOfferSchema : franchiseSchema,
    onSubmit: async (values) => {
      // Normalize phone to digits only to avoid duplicate errors due to formatting
      const sanitizedPhone = String(values?.contact || "").replace(/\D/g, "");

      // Pre-check for duplicate phone for this merchant
      try {
        const existing = await dispatch(
          getFranchises({ page: 1, search: sanitizedPhone })
        ).unwrap();
        const exists = Array.isArray(existing?.data)
          ? existing.data.some((loc) => String(loc?.phone || "").replace(/\D/g, "") === sanitizedPhone)
          : false;

        // Verify we are not matching our own store (if editing)
        const isSelf = id && exists && existing.data.some(loc => loc._id === id && String(loc?.phone || "").replace(/\D/g, "") === sanitizedPhone);

        if (exists && !isSelf) {
          toast.error(
            "A store with this phone number already exists. Please use a different phone or update the existing store."
          );
          return;
        }
      } catch (_) { }

      if (id !== "" && id !== null) {
        const editPromise = await dispatch(
          editFranchise({
            id: id,
            name: values?.franchiseName,
            email: values?.email,
            phone: sanitizedPhone,
            phoneCountry: values?.contactCountry,
            googlePlaceId: placeId !== null ? placeId : null,
            tradingHours: values.timeData
              .filter((day) => day?.isSelected === true)
              .map((day) => ({
                day: day?.day?.toLowerCase(),
                openTime: day?.openTime ? day?.openTime.format(format) : null,
                closeTime: day?.closeTime
                  ? day?.closeTime.format(format)
                  : null,
              })),
          })
        ).unwrap();
        if (editPromise?.success === true) {
          navigate("/merchant/franchise-list");
        }
      } else {
        const addPromise = await dispatch(
          addFranchise({
            name: values?.franchiseName,
            email: values?.email,
            phone: sanitizedPhone,
            phoneCountry: values?.contactCountry,
            googlePlaceId: placeId !== null ? placeId : null,
            tradingHours: values.timeData
              .filter((day) => day?.isSelected === true)
              .map((day) => ({
                day: day?.day?.toLowerCase(),
                openTime: day?.openTime ? day?.openTime.format(format) : null,
                closeTime: day?.closeTime
                  ? day?.closeTime.format(format)
                  : null,
              })),
          })
        ).unwrap();
        if (addPromise?.success === true) {
          navigate("/merchant/franchise-list");
        }
      }
    },
  });

  const { document, timeData } = formik.values;

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    setFieldValue,
    dirty,
    isValid
  } = formik;

  const handleTimeChange = (dayIndex, field, time) => {
    const updatedTimeData = timeData.map((item, idx) => {
      if (idx === dayIndex) {
        return {
          ...item,
          [field]: time,
        };
      }
      return { ...item };
    });
    setFieldValue("timeData", updatedTimeData);
  };

  useEffect(() => {
    const allTimeSet = timeData?.every((day) => day.isSelected === false);
    setFieldValue("timeSet", allTimeSet);
  }, [timeData, setFieldValue]);

  const items = [
    {
      title: "Other Stores",
    },
    {
      title: id ? "Edit Store" : "Add Store",
    },
  ];

  const [placeId, setPlaceId] = useState(null);

  // const handleUploadClick = () => {
  //   setUploadClicked(true);
  // };

  // const handleAddClick = () => {
  //   setUploadClicked(false);
  // };

  const handleCancelClick = () => {
    navigate("/merchant/franchise-list");
  };

  useEffect(() => {
    if (id !== "" && id !== null) {
      const franchise = allFranchises?.find((f) => f?._id === id);
      if (franchise) {
        const newTimeData = days.map((day) => {
          const dayLower = day.toLowerCase();
          const foundHour = franchise?.tradingHours?.find(
            (th) => th?.day === dayLower
          );

          if (foundHour) {
            return {
              day: dayLower,
              openTime: dayjs(foundHour?.openTime, format),
              closeTime: dayjs(foundHour?.closeTime, format),
              isSelected: true,
            };
          }
          return {
            day: dayLower,
            openTime: undefined,
            closeTime: undefined,
            isSelected: false,
          };
        });

        // Set placeId state if needed for later use
        if (franchise?.googlePlaceId?.placeId) {
          setPlaceId(franchise.googlePlaceId.placeId);
        }

        const { phone: normalizedContact, country: inferredCountry } =
          normalizePhoneNumberForInput(franchise?.phone, "AU");

        setInitialValues({
          document: "",
          franchiseName: franchise?.name || "",
          email: franchise?.email || "",
          contact: normalizedContact,
          contactCountry: inferredCountry,
          merchantName: franchise?.merchant?.legalName || "",
          address: franchise?.googlePlaceId?.address || "",
          timeSet: false, // Calculated by useEffect based on timeData
          timeData: newTimeData,
        });
      }
    }
  }, [id, allFranchises]);

  // file validation handled inline in CSV handler

  const handleAcceptedFiles = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isCSV = file.name.toLowerCase().endsWith(".csv");
    if (!isCSV) {
      toast.error("Only .csv files are allowed!");
      handleRejectedFiles([file]);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
      if (lines.length < 2) {
        toast.error("CSV must have headers and at least one row.");
        return;
      }

      // Robust CSV splitter: respects quoted fields with commas
      const splitCsvLine = (line) => {
        const matches = line.trim().match(/("[^"]*"|[^,]+)(?=,|$)/g);
        if (!matches) return [];
        return matches.map((m) => m.replace(/^"|"$/g, "").trim());
      };

      const headers = (splitCsvLine(lines[0]) || [])
        .map((h) => h.trim().toLowerCase().replace(/\r/g, ""));

      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      // Build rows
      const rawRows = lines.slice(1).map((line, index) => {
        const values = splitCsvLine(line).map((v) => v.trim().replace(/\r/g, ""));
        const row = {};
        headers.forEach((key, i) => {
          row[key] = values[i] || "";
        });

        const tradingHours = days.map((day) => ({
          day,
          openTime: row[`${day}_open`] ?? "",
          closeTime: row[`${day}_close`] ?? "",
        }));

        const phoneDigits = String(row["phone"] || "").replace(/\D/g, "");

        return {
          name: row["name"] || "",
          email: row["email"] || "",
          phone: phoneDigits,
          address: row["address"] || "",
          tradingHours,
          // Note: header normalized to lowercase, so use 'googleplaceid'
          googlePlaceId: row["googleplaceid"] || null,
          _id: `temp_${Date.now()}_${index}`,
        };
      });

      // Filter out empty rows and deduplicate by phone within the file
      const seenPhones = new Set();
      const data = rawRows
        .filter((r) => r.name && (r.address || r.googlePlaceId))
        .filter((r) => {
          if (!r.phone) return true; // allow rows without phone to proceed
          if (seenPhones.has(r.phone)) return false;
          seenPhones.add(r.phone);
          return true;
        });

      console.log("Final Parsed Data:", data);

      // Call API; rely on Redux slice to toast on failure to avoid duplicates
      (async () => {
        const resultAction = await dispatch(addFranchise({ locations: data }));
        const payload = resultAction?.payload;
        if (payload?.success === true) {
          setFieldValue("document", file.name);
          toast.success(`${data.length} location(s) parsed successfully`);
        }
      })();
    };

    reader.onerror = () => {
      toast.error("Error reading the CSV file.");
      handleRejectedFiles([file]);
    };

    reader.readAsText(file);
  };

  const handleRejectedFiles = (files) => {
    // const file = files[0]; // used for message only
    setFieldValue("document", null);
    if (files[0]?.errors) {
      files[0]?.errors?.forEach((error) => {
        toast.error(error.message);
      });
    } else {
      toast.error("Invalid file format. Please upload a valid CSV file.");
    }
  };

  const handleClear = () => {
    setFieldValue("document", "");
    // Remove temporary locations that were added via CSV
    // const tempLocations = selectedLocations.filter((loc) =>
    //   loc._id.startsWith("temp_")
    // );
    // const tempLocationNames = tempLocations.map((loc) => loc.name);

    // setSelectedLocations((prev) =>
    //   prev.filter((loc) => !loc._id.startsWith("temp_"))
    // );
    // setSelectedLocationIds((prev) =>
    //   prev.filter((id) => !id.startsWith("temp_"))
    // );
    // setLocationNames((prev) =>
    //   prev.filter((name) => !tempLocationNames.includes(name))
    // );
    // setLocations((prev) => prev.filter((loc) => !loc._id.startsWith("temp_")));
  };

  return (
    <>
      {loading === true && <Loader />}
      <div className="px-4 md:px-0">
        <div className="w-full flex flex-col md:flex-row justify-between mb-4 md:mb-0 gap-4 md:gap-0">
          <BreadCrumsHeader heading={id ? "Edit a Store" : "Add a Store"} items={items} form={true} />
          <div className="w-full md:w-[60%] lg:w-[50%] xl:w-[40%]">
            <CSVUpload
              document={document}
              handleAcceptedFiles={handleAcceptedFiles}
              handleClear={handleClear}
              sampleFileUrl="/sample-locations.csv"
            // offerCodesCount={selectedLocations.length}
            />
          </div>
        </div>
      </div>
      <FormLayout
        title={"Store Information"}
        content={
          <div className="px-0 md:px-5">
            {/* <div className="mt-5 w-full flex justify-between items-center">
              <div
                className="flex items-center gap-2 cursor-pointer"
                onClick={handleAddClick}
              >
                <AddIcon />
                <p className="font-poppins font-[400] sm:text-[12px] md:text-[14px] text-[#00ADEF] text-center leading-[18px]">
                  Add Location
                </p>
              </div>
              <UploadIcon
                className="cursor-pointer"
                onClick={handleUploadClick}
              />
            </div> */}
            <AddStore
              values={formik.values}
              errors={errors}
              touched={touched}
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              handleTimeChange={handleTimeChange}
              format={format}
              uploadClicked={uploadClicked}
              setPlaceId={setPlaceId}
            />
          </div>
        }
      />
      <div className="col-span-12 mt-14 px-4 sm:px-0 flex justify-between md:justify-end gap-4">
        <button
          className="w-[48%] md:w-[15%] h-[45px] rounded-[5px] bg-[#F0F0F0] text-[16px] font-[500] text-[#8C8C8C]"
          style={{ border: "1px solid #C4C4C4" }}
          onClick={handleCancelClick}
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
          className={`w-[48%] md:w-[15%] h-[45px] rounded-[5px] text-[16px] font-[500] text-[#FFFFFF] ${loading ? 'bg-[#C4C4C4] cursor-not-allowed' : 'bg-[#00ADEF]'}`}
        >
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </>
  );
};

export default StoreFormPage;
